import { useCallback, useState } from 'react';
import i18n from '../../i18n';
import { duplicateDocument, hasSubpages, copyDocumentLink, togglePin } from '../../extensions/bundled/documents/documentsActions';
import { useNotificationStore } from '../../store/notificationStore';
import { openDocumentTab } from '../../store/tabsStore';
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { errorMessage } from '../../utils/errorMessage';
import { exportDocument } from '../documentExport';
import { printDocument } from '../DocumentPrintView';
import { exportFolderMarkdown } from './exportFolderMarkdown';

/**
 * The row menu's actions (card 3.3), wired to the store layer
 * (documentsActions.js, card 3.4) and the existing export / print handlers.
 * Dialog state for Duplicate (asked only when the page has sub-pages) and
 * Move to… lives here; TreeSection renders the dialogs.
 */
export default function useRowCommands({ sectionId, scope, tree, actions, exporting, pins, setEditing }) {
  const [duplicateAsk, setDuplicateAsk] = useState(null); // node | null
  const [moveNodeAsk, setMoveNodeAsk] = useState(null); // node | null
  const { load, nodes } = tree;

  const notes = () => useNotificationStore.getState();

  const runDuplicate = useCallback(
    async (node, includeSubpages) => {
      setDuplicateAsk(null);
      try {
        const { id } = await duplicateDocument(node.id, scope, { includeSubpages });
        notifyMcpSurfaceChanged('documents');
        await load();
        const title = node.name ? i18n.t('docStore.copyTitle', { title: node.name }) : i18n.t('docStore.untitledCopy');
        openDocumentTab({ documentId: id, label: title, scope });
      } catch (err) {
        // Plans v2: the upgrade modal already says why; no second error.
        if (err?.planLimit) return;
        notes().error(errorMessage(err, i18n.t('docSidebar.errors.duplicate')), i18n.t('docSidebar.menu.duplicate'));
      }
    },
    [scope, load]
  );

  const cmds = {
    newDocument: (parentId) => actions.handleNewDocument(parentId),
    newFolder: (parentId) => actions.handleNewFolder(parentId),
    rename: (node) => setEditing({ sectionId, id: node.id }),
    duplicate: (node) => (hasSubpages(nodes, node.id) ? setDuplicateAsk(node) : runDuplicate(node, false)),
    moveTo: (node) => setMoveNodeAsk(node),
    togglePin: async (node) => {
      try {
        await togglePin(node.id, scope);
        await pins.reload();
      } catch (err) {
        notes().error(errorMessage(err, i18n.t('docSidebar.errors.pin')), i18n.t('docSidebar.pinned'));
      }
    },
    copyLink: (node) => copyDocumentLink(node.id, scope),
    exportDocument: (node, format) => exporting.withDocumentBody(node, (doc) => exportDocument(format, doc)),
    print: (node) => exporting.withDocumentBody(node, (doc) => printDocument(doc)),
    exportFolderPdf: (node) => exporting.openFolderPdfOptions(node),
    exportFolderMarkdown: async (node) => {
      try {
        const res = await exportFolderMarkdown({ nodes, folder: node, scope });
        if (res?.empty) notes().info(i18n.t('docSidebar.export.folderEmpty'), node.name || i18n.t('docSidebar.untitledFolder'));
      } catch (err) {
        notes().error(errorMessage(err, i18n.t('docSidebar.errors.export')), i18n.t('docSidebar.menu.exportFolder'));
      }
    },
    trash: (node) => actions.handleDelete(node),
  };

  return { cmds, duplicateAsk, setDuplicateAsk, runDuplicate, moveNodeAsk, setMoveNodeAsk };
}
